import { useEffect, useMemo, useState } from 'react';
import { Alert, Col, Container, Row, Spinner } from 'react-bootstrap';
import { Save } from 'lucide-react';
import { Navigate, useParams } from 'react-router-dom';
import type { QuestionnaireQuestion } from '../../../../infrastructure/QuestionnaireClient';
import { ROUTES } from '../../../../routes/routes';
import { QuestionBuilder } from '../../../questionnaire/components/QuestionBuilder';
import { SurveyPreview } from '../../../questionnaire/components/SurveyPreview';
import { SurveyDetailTabs, SurveyHeader } from '../../components';
import { useSurveyDetail } from '../../hooks/useSurveyAdminData';
import { createBlankQuiz, createSurveyDeployRequest, ensureQuizIds } from '../../utils/surveyForm';

export default function SurveyQuizPage() {
  const { surveyId: surveyIdParam } = useParams();
  const { client, survey, surveyId, isNew, isLoading, error, reload } = useSurveyDetail(surveyIdParam);
  const [quiz, setQuiz] = useState<QuestionnaireQuestion[]>(() => createBlankQuiz());
  const [message, setMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (survey?.latestVersion?.schema.questions) {
      setQuiz(survey.latestVersion.schema.questions);
    }
  }, [survey]);

  const deployRequest = useMemo(() => {
    if (!survey) return null;
    return createSurveyDeployRequest(
      {
        title: survey.title,
        formType: survey.formType,
      },
      { questions: ensureQuizIds(quiz) },
    );
  }, [quiz, survey]);

  const handleDeploy = async () => {
    if (!surveyId || !deployRequest) return;

    setIsSaving(true);
    setMessage(null);
    setSaveError(null);
    try {
      const deployed = await client.deployVersion(surveyId, deployRequest);
      setMessage(`Quiz deployed as survey v${deployed.latestVersion?.version ?? '-'}`);
      await reload();
    } catch (deployError) {
      setSaveError(deployError instanceof Error ? deployError.message : 'Failed to deploy quiz');
    } finally {
      setIsSaving(false);
    }
  };

  if (isNew) {
    return <Navigate to={ROUTES.survey.settings('new')} replace />;
  }

  return (
    <Container fluid className="p-4">
      <SurveyHeader survey={survey} />
      {surveyId && <SurveyDetailTabs surveyId={surveyId} />}

      {error && <Alert variant="danger">{error}</Alert>}
      {message && <Alert variant="success">{message}</Alert>}
      {saveError && <Alert variant="danger">{saveError}</Alert>}

      <section className="border rounded p-3 bg-white">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h2 className="h5 mb-0">Quiz</h2>
          <button
            className="btn btn-primary btn-sm"
            type="button"
            onClick={() => void handleDeploy()}
            disabled={isLoading || isSaving || !deployRequest}
          >
            {isSaving ? <Spinner animation="border" size="sm" className="me-1" /> : <Save size={15} className="me-1" />}
            Save & Deploy
          </button>
        </div>
        <Row className="g-3">
          <Col xl={7}>
            <QuestionBuilder questions={quiz} onChange={setQuiz} readOnly={false} />
          </Col>
          <Col xl={5}>
            <SurveyPreview questions={ensureQuizIds(quiz)} />
          </Col>
        </Row>
      </section>
    </Container>
  );
}
